import { create } from 'zustand';

interface AnalyticsState {
  sessionId: string;
  pageViewId: string | null;
  sectionTimers: Map<string, { start: number; accumulated: number }>;

  initSession: () => void;
  setSectionVisible: (sectionId: string) => void;
  setSectionHidden: (sectionId: string) => void;
  getPageViewId: () => string | null;
  setPageViewId: (id: string) => void;
  flushSections: (auctionId: string) => Array<{ section_id: string; focus_seconds: number }>;
}

function generateSessionId(): string {
  let stored = sessionStorage.getItem('analytics_session_id');
  if (!stored) {
    stored = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', stored);
  }
  return stored;
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  sessionId: generateSessionId(),
  pageViewId: null,
  sectionTimers: new Map(),

  initSession: () => {
    // noop, session already set
  },

  setSectionVisible: (sectionId) =>
    set((state) => {
      const timers = new Map(state.sectionTimers);
      if (!timers.has(sectionId)) {
        timers.set(sectionId, { start: Date.now(), accumulated: 0 });
      }
      return { sectionTimers: timers };
    }),

  setSectionHidden: (sectionId) =>
    set((state) => {
      const timers = new Map(state.sectionTimers);
      const timer = timers.get(sectionId);
      if (timer) {
        const elapsed = Math.floor((Date.now() - timer.start) / 1000);
        timers.set(sectionId, {
          start: 0,
          accumulated: timer.accumulated + elapsed,
        });
      }
      return { sectionTimers: timers };
    }),

  setPageViewId: (id) => set({ pageViewId: id }),
  getPageViewId: () => get().pageViewId,

  flushSections: (auctionId) => {
    const { sectionTimers } = get();
    const sections: Array<{ section_id: string; focus_seconds: number }> = [];

    for (const [sectionId, timer] of sectionTimers) {
      const total = timer.start > 0
        ? timer.accumulated + Math.floor((Date.now() - timer.start) / 1000)
        : timer.accumulated;

      if (total > 0) {
        sections.push({ section_id: sectionId, focus_seconds: Math.min(total, 3600) });
      }
    }

    // Reset timers
    set({ sectionTimers: new Map() });
    return sections;
  },
}));
